import { stammdatenService } from './StammdatenService.js';

const LEERES_WETTER = { ort: undefined, vorhersage: {} };

/**
 * Lädt die Wettervorhersage (siehe backend/wetterRouter.js -> infrastructure/WeatherService.js).
 * Standort: PLZ/Ort der aktuellen Betriebsstätte; falls dafür nichts gefunden wird, die
 * Browser-Geolocation (mit Berechtigungsabfrage). Klappt beides nicht, gibt es kein Wetter.
 * Rein dekorativ: ein Fehlschlag hier darf niemals das Bearbeiten von Schichten stören, daher
 * gibt es hier — anders als bei den anderen Services — bewusst KEINE zeigeKleineNachricht-Toasts
 * bei Fehlern, nur ein console.log.
 */
class WetterService {
	constructor() {
		// Betriebsstätte-ID -> Promise<{ ort, vorhersage }>. Das Promise (nicht das Ergebnis) wird
		// gecacht, damit gleichzeitige Grid-Renderings nur eine Anfrage bzw. eine Geolocation-Abfrage auslösen.
		this.cache = new Map();
	}

	/**
	 * @param {boolean} refresh erzwingt ein Neuladen, ignoriert den In-Memory Cache dieser Instanz
	 * @returns {object} { ort, vorhersage: { [YYYY-MM-DD]: { 8: {code, tempC}, 13: {...}, 18: {...} } } },
	 * bei Fehler { ort: undefined, vorhersage: {} }
	 */
	async getWetter(refresh = false) {
		const betriebID = stammdatenService.aktuelleBetriebsstaette;
		if (refresh || !this.cache.has(betriebID)) {
			this.cache.set(betriebID, this._ladeWetter());
		}
		return this.cache.get(betriebID);
	}

	async _ladeWetter() {
		const unternehmen = await stammdatenService.getUnternehmensdaten();
		const betrieb = unternehmen?.Betriebsstaette?.find((bs) => bs._id === stammdatenService.aktuelleBetriebsstaette);
		const plz = String(betrieb?.PLZ ?? '').trim();
		const ort = String(betrieb?.Ort ?? '').trim();
		if (plz || ort) {
			const params = new URLSearchParams();
			if (plz) {
				params.set('PLZ', plz);
			}
			if (ort) {
				params.set('Ort', ort);
			}
			const ergebnis = await this._fetchWetter(params);
			if (ergebnis) {
				return ergebnis;
			}
		}
		const position = await this._leseGeoPosition();
		if (position) {
			const ergebnis = await this._fetchWetter(new URLSearchParams({ lat: position.lat, lon: position.lon }));
			if (ergebnis) {
				return { ort: 'Aktueller Standort', vorhersage: ergebnis.vorhersage };
			}
		}
		return LEERES_WETTER;
	}

	async _fetchWetter(params) {
		try {
			const response = await fetch(`/dienstplan/api/wetter?${params.toString()}`);
			if (response.ok) {
				return await response.json();
			}
			return undefined;
		} catch (error) {
			console.log(error);
			return undefined;
		}
	}

	/**
	 * Fragt die Browser-Geolocation ab (löst ggf. die Berechtigungsabfrage aus). Funktioniert nur im
	 * Secure Context (https/localhost).
	 * @returns {Promise<object|undefined>} { lat, lon } oder undefined bei Ablehnung/Fehler
	 */
	_leseGeoPosition() {
		return new Promise((resolve) => {
			if (!navigator.geolocation) {
				resolve(undefined);
				return;
			}
			navigator.geolocation.getCurrentPosition(
				(position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
				(error) => {
					console.log(error);
					resolve(undefined);
				},
				{ maximumAge: 1000 * 60 * 60, timeout: 10000 },
			);
		});
	}
}

const wetterService = new WetterService();
export { wetterService };
